import { Location } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { LoggingService } from "@core/services";
import { Progress } from "@modules/common";
import { NgbNav, NgbNavChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import {
  addressName,
  CustomerAddressDto,
  CustomerContactDto,
  CustomerMasterDto,
  JobDto,
  LookupObjectDto,
  MachineMasterDto,
  newCustomerAddress,
  newCustomerContact,
  newCustomerMaster,
  newJob,
  newMachineMaster
} from "@shared/models";
import { from } from "rxjs";
import { switchMap } from "rxjs/operators";
import { AppQuery, LoginStatus } from "../../common/app.store";
import {
  BaseFormDirective,
  ContactComponent,
  MachineMasterComponent
} from "../../common/components";
import {
  CustomerService,
  FileService,
  FilesUploader,
  JobService,
  MachineService,
  ModelService,
  ProductSettingService,
  UploadFile,
  WebLayoutService
} from "../../common/services";

@Component({
  templateUrl: "./create.component.html",
  styleUrls: ["./create.component.scss"],
  selector: "abi-customer-create"
})
export class CustomerCreateComponent extends BaseFormDirective implements OnInit {
  @ViewChild(NgbNav) tabs: NgbNav;
  @ViewChild(ContactComponent) contactComponent: ContactComponent;
  @ViewChild(MachineMasterComponent) machineComponent: MachineMasterComponent;
  @ViewChild("submitted") submittedModal;
  @ViewChild("navContent", {read: ElementRef}) navContent: ElementRef;
  isDebug = false;

  customer: CustomerMasterDto;
  confirmCustomer: CustomerMasterDto = newCustomerMaster(); // just for displaying on the confirmation page
  machines: MachineMasterDto[];
  job: JobDto;
  machineForm: FormGroup;
  activePage: string;
  pages: string;
  loggedIn = false;
  canSchedule = false;
  lastPageId: string;

  sources: { code: string; description: string }[];
  firstPage = true;
  lastPage = false;
  confirmPage = false;
  submitting = false;

  fileUploadEnabled = false;
  uploadingFiles = false;
  uploadProgress = 0;
  files: FilesUploader;

  validationMessages = {
    ...MachineMasterComponent.validationMessages,
  };

  get machineAttachments(): UploadFile[] {
    return this.machineForm.controls.attachment?.value;
  }

  initComplete: boolean = false;
  filterModelsBy: string;

  // defaultRegistrationTabs = ["contact", "machine"];
  defaultRegistrationTabs = ["contact", "address", "machine", "dealerContact"];
  registrationTabs = this.defaultRegistrationTabs;
  constructor(
    protected layoutService: WebLayoutService,
    private route: ActivatedRoute,
    fb: FormBuilder,
    private productSettings: ProductSettingService,
    private jobService: JobService,
    private customerService: CustomerService,
    private appQuery: AppQuery,
    private dialog: MatDialog,
    private location: Location,
    private fileService: FileService,
    private machineService: MachineService,
    private loggingService: LoggingService,
    private modelService: ModelService
  ) {
    super(layoutService, fb);
  }

  public jobTypeFilter(item: LookupObjectDto): boolean {
    const loggedIn = this.appQuery.loginStatus === LoginStatus.True;
    return (
      (loggedIn && item.order < 100) ||
      (!loggedIn && item.order >= 100) ||
      item.order === 0
    );
  }

  // note that the visibility can be overridden by ProductRegistrationTabs
  // dealerVisibilitySources: string[] = ["CON", "AGENT", "TECH", "DEAL", "OTHER"];
  dealerVisibilitySources: string[] = ["AGENT", "DEAL"];// similar setup as we do in CreateJobComponent
  dealerValidationSources: string[] = ["DEAL"];
  createRegistrationJob = false;
  sourceId: string = "";
  jobTypeId: string = "REG";
  ngOnInit() {
    super.ngOnInit();
    this.loggedIn = this.appQuery.loginStatus === LoginStatus.True;
    this.canSchedule = this.appQuery.hasRole("CC");
    this.registrationTabs = this.productSettings.arrayValue("ProductRegistrationTabs", this.defaultRegistrationTabs);
    this.dealerVisibilitySources = this.productSettings.arrayValue("DealerVisibilitySources", this.dealerVisibilitySources);
    this.dealerValidationSources = this.productSettings.arrayValue("DealerValidationSources", this.dealerValidationSources);
    this.createRegistrationJob = this.productSettings.booleanValue("ProductRegistrationCreateJob") || this.createRegistrationJob;
    this.jobTypeId = this.productSettings.stringValue("ProductRegistrationCreateJob") || this.jobTypeId;

    // Apply Models filter (for registration campaigns)
    this.route.data.subscribe(routeData => {
      if (routeData.filterModelsBy)
        this.filterModelsBy = routeData.filterModelsBy;
    });

    // Query Existing Data from Params
    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          return from(
            this.configureAll(
              params.get("customerId"),
              params.get("contactId"),
              params.get("lastname"),
              params.get("firstname"),
              params.get("telephone"),
              // These params are used if we need to create a Job (as opposed to a Customer/Machine record)
              params.get("sourceId"),
              params.get("typeId"),
              params.get("modelId"),
              params.get("serialNumber"),
              params.get("machineField1"),
              params.get("machineField2"),
              params.get("machineField3"),
              params.get("machineField4"),
              params.get("machineField5"),
              // NOTE: could also pass in Job related fields here for extra customiseability
              params.get("debug")
            )
          );
        })
      )
      .subscribe((customer) => {
        this.customer = customer;
        this.jobService.loadCategories().subscribe(val => {
          this.initComplete = true;
        });
      });
  }

  get dealerTabVisible() {
    return this.registrationTabs.includes('dealerContact') && this.dealerVisibilitySources.some(source => this.source.includes(source));
  }

  get dealerValidate() {
    return this.dealerValidationSources.some(source => this.source.includes(source));
  }

  get source(){
    return this.form?.controls.sourceId.value || this.sourceId || "UNDEFINED";
  }

  get customerContactType() {
    // get contact type from some mapping/settings
    return this.productSettings.stringValue('ProductRegistrationCustomerType_'+this.source) || 'Customer';
  }

  createForm() {
    this.machineForm = MachineMasterComponent.createFormGroup(this.formBuilder);
    this.form = this.formBuilder.group({
      machineMaster: this.machineForm,
      contact: null,
      machine: null,
      sourceId: null,// only used for dealer validation (and recording on REG-Jobs if enabled)
      addressId: null,
      address: null,
      dealerContact: null,
    });
    super.createForm();
  }

  private configureAll(
    customerId: string,
    contactId: string,
    lastname: string,
    firstname: string,
    telephone: string,
    sourceId: string,
    typeId: string,
    modelId: string,
    serialNumber: string,
    machineField1: string,
    machineField2: string,
    machineField3: string,
    machineField4: string,
    machineField5: string,
    debug: string
  ): Promise<CustomerMasterDto> {
    if (debug) {
      this.isDebug = true;
    }
    let promise: Promise<CustomerMasterDto>;
    let user = this.appQuery.username;
    if (this.appQuery.roles.includes("TECH") && this.appQuery.defaultResource && this.appQuery.defaultResource !== "*")
    {
      user = this.appQuery.defaultResource + " " + user;
    }
    const newContact = newCustomerContact();
    newContact.firstName = firstname;
    newContact.lastName = lastname;
    newContact.mobile = telephone;
    newContact.contactId = "NEW";

    if (customerId) {
      promise = this.customerService
        .getCustomer(customerId)
        .toPromise()
        .then((customer) => {
          for (const machine of customer.machines) {
            machine.model = customer.models.find(
              (m) => m.code === machine.modelId
            );
          }
          return customer;
        });
    } else {
      promise = Promise.resolve(newCustomerMaster()).then((customer) => {
        return customer;
      });
    }

    this.sourceId = sourceId;// get an immediate source for initial setup - source can be changed later via the form
    // CODE SMELL: were setting a customer DTO, then were applying that to a FORM,
    // then were using the form data to generate a new Customer DTO... setFormData/getFormData?
    return promise.then((customer) => {

      // Create pre-liminary Job (might not be used in the end - depends on configuration)
      this.job = this.jobService.jobFactory();
      this.job.typeId = typeId || this.jobTypeId;

      if(modelId){
        this.modelService.queryModels(null, modelId).subscribe(models => {
          // this.job.model = models[0];
          const newMachine = newMachineMaster("A");
          newMachine.model = models[0];
          newMachine.modelId = modelId;
          newMachine.serialNumber = serialNumber;
          newMachine.fields[0] = machineField1;
          newMachine.fields[1] = machineField2;
          newMachine.fields[2] = machineField3;
          newMachine.fields[3] = machineField4;
          newMachine.fields[4] = machineField5;
          // machine.categories = models[0].categories;
          newMachine.name = models[0].description;

          MachineMasterComponent.setFormData(
            newMachine,
            this.form.get("machineMaster") as FormGroup,
            this.productSettings
          );
        });
      }

      // populate machine with model and fields
      const machine = newMachineMaster("A");
      machine.modelId = modelId;
      machine.serialNumber = serialNumber;
      machine.fields[0] = machineField1;
      machine.fields[1] = machineField2;
      machine.fields[2] = machineField3;
      machine.fields[3] = machineField4;
      machine.fields[4] = machineField5;

      // FORM POPULATION
      this.form.patchValue({
        contact: customer.contacts.length ? customer.contacts.find((c) => c.contactId === contactId) : newContact,
        address: customer.addresses.length ? customer.addresses[0] : newCustomerAddress(),
        sourceId: sourceId ?? null,
        dealerContact: this.dealerTabVisible ? newCustomerContact() : null,
        // machineMaster: machine,
      });

      // When using a MachineMasterComponent, we need to set the form data using it's static methods
      MachineMasterComponent.setFormData(
        machine,
        this.form.get("machineMaster") as FormGroup,
        this.productSettings
      );

      this.formValueChanged();

      return customer;
    });
  }

  private canChange(activeId: string, nextId: string): boolean {
    let group: AbstractControl;
    switch (activeId) {
      case "contact":
        group = this.form.get("contact");
        break;
      case "machine":
        group = this.machineForm;
        break;
    }
    const tabs = this.tabs.items.toArray();
    const oldId = tabs.findIndex((t) => t.id === activeId);
    const newId = tabs.findIndex((t) => t.id === nextId);
    if (newId > oldId && group) {
      if (group instanceof FormGroup) {
        this.validateGroup(group, "", true);
        return group.valid;
      }
      group.markAsDirty();
      return group.valid;
    }
    return true;
  }

  public beforeChange($event: NgbNavChangeEvent) {
    if (!this.canChange($event.activeId, $event.nextId))
      $event.preventDefault();
  }

  public afterChange(newId: any) {
    if (this.tabs) {
      this.firstPage = this.tabs.items.first.id === newId;
      this.lastPage = this.tabs.items.last.id === newId || newId === this.lastPageId;
      // this.navContent.nativeElement.focus();
    }
  }

  finish() {
    if (!this.confirmPage) {
      this.confirmPage = this.validateRegistration();
    } else if (!this.submitting) {
      this.submitJob();
    }
    return false;
  }

  previousPage() {
    const items = this.tabs.items.toArray();
    let cId: number;
    if (this.lastPage && this.confirmPage)
      cId = items.findIndex((t) => t.id === this.lastPageId) + 1;
    else
      cId = items.findIndex((t) => t.id === this.tabs.activeId);
    this.lastPage = this.confirmPage;
    this.confirmPage = false;
    if (cId > 0) {
      const nextId = items[cId - 1].id;
      if (this.canChange(this.tabs.activeId, nextId)) this.tabs.select(nextId);
    }
    return false;
  }

  nextPage() {
    const items = this.tabs.items.toArray();
    const cId = items.findIndex((t) => t.id === this.tabs.activeId);
    if (cId < items.length - 1) {
      const nextId = items[cId + 1].id;
      if (this.canChange(this.tabs.activeId, nextId)) this.tabs.select(nextId);
    }

    return false;
  }

  cancel() {
    this.location.back();
    return false;
  }

  contactChanged(contact: CustomerContactDto) {
    this.form.patchValue({contact});
  }

  addMachineTo(customer: CustomerMasterDto){
    const currentMachine: MachineMasterDto = newMachineMaster("A");
    MachineMasterComponent.getFormData(
      currentMachine,
      this.form.controls.machineMaster as FormGroup
    );
    customer.machines.push(currentMachine);
  }

  addOrUpdateContactTo(customer: CustomerMasterDto) {
    const values = this.form.getRawValue();
    if (values.contact?.contactId && values.contact?.contactId !== 'NEW') {
      // its a contact update
      const contactIndex = customer.contacts.findIndex(item => item.contactId === values.contact.contactId);
      const originalContact = customer.contacts[contactIndex];
      customer.contacts[contactIndex] = { ...originalContact, ...values.contact };
    } else {
      customer.contacts.push(values.contact);
    }
  }

  private deepClone(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  private validateRegistration(): boolean {
    try {
      this.validate(true);
      if (this.form.valid) {


        if(this.createRegistrationJob){
          const formData = this.form.getRawValue();
          // Registration Job Creation
          const job = this.job;
          job.contact = formData.contact;
          job.address = formData.address;
          MachineMasterComponent.getFormData(
            job.machine,
            this.form.controls.machineMaster as FormGroup
          );
          job.dealerContact = formData.dealerContact;
          job.sourceId = formData.sourceId;
          // job.consent = values.source.consent;
          // job.faultDescription = values.details.faultDescription;
          // job.faultCodeId = codeOnly(values.details.faultCodeId);
          // job.typeId = codeOnly(values.details.typeId);// already set in the beginning
          // job.diagnosticCodeId = codeOnly(values.details.diagnosticCodeId);
          // job.customerRef = values.details.customerRef;
          // job.field1 = codeOnly(values.details.field1);
          // job.field2 = codeOnly(values.details.field2);
          // job.field3 = codeOnly(values.details.field3);
          // job.field4 = codeOnly(values.details.field4);
          // job.field5 = codeOnly(values.details.field5);
          // job.machine.journals = job.machine.journals?.filter(j => j.isNew) || [];
          // return true;

          // this.job.customer = this.confirmCustomer;
          // this.job.contact = this.confirmCustomer.contacts[0];
          // this.job.sourceId = this.form.controls.sourceId.value;
          // this.jobService.createJob(this.job, this.isPublic).subscribe();
        } else {
          // Standard Customer/Machine creation
          this.confirmCustomer = this.deepClone(this.customer); // can't shallow clone as the contacts/machines refs still transfer
          this.addOrUpdateContactTo(this.confirmCustomer);
          this.confirmCustomer.machines = []; // always add new machine
          this.addMachineTo(this.confirmCustomer);

        }
        return true;
      }
    } catch (e) {
      console.error(e);
    }
    return false;
  }

  get loading() {
    return this.submitting || this.uploadingFiles || !this.initComplete;
  }

  // TODO: either use a 'custom' redirect text in a dialog or just the plain old dialog
  openFinishedDialog() {
    // Show the Confirmation Modal
    const ref = this.dialog.open(this.submittedModal);
    ref.afterClosed().subscribe((closeValue) => {
      // an undefine/null closevalue - just means - no click on a button

      // Raleigh Specific Code
      // Sometimes we'd like to redirect elsewhere other than back to the Job view (when logged out)
      if (closeValue && this.productSettings.hasSetting("JobPublicRedirect")) {
        this.submitting = true;
        if ( window.location !== window.parent.location ) {
          window.parent.location.href = this.productSettings.stringValue("JobPublicRedirect");
        } else {
          window.location.href = this.productSettings.stringValue("JobPublicRedirect");
        }
      } else {
        this.location.back();
      }
    });
  }

  createOrUpdateCustomer() {
    const newCustomerDto = this.deepClone(this.customer);
    this.addOrUpdateContactTo(newCustomerDto);
    this.addMachineTo(newCustomerDto);
    if (newCustomerDto.id) {
      return this.customerService.updateCustomer(newCustomerDto, this.isPublic)
      .toPromise();
    } else {
      return this.customerService
        .createNewCustomer(newCustomerDto)
        .toPromise();
    }
  }

  submitJob() {
    this.submitting = true;
    try {
      if(this.createRegistrationJob) {
        // console.log(this.job);return;
        this.jobService.newJob(this.job, this.isPublic).toPromise()
        .then(
          (response) => {
            this.submitting = false;
            if (this.shouldUploadFiles()){
              this.processFileUploads(response.machine.machineId, () => {
                this.openFinishedDialog();
              });
            } else {
              this.openFinishedDialog();
            }
          },
          (error) => {
            this.submitting = false;
          }
        );
      } else {
        this.createOrUpdateCustomer()
          .then(
            (response) => {
              this.submitting = false;
              if (this.shouldUploadFiles()){
                // TODO: note that 'response' may or may not be a CustomerDto or a JobDto... to be confirmed
                const machines = response.machines;
                const machineId = machines[machines.length - 1].machineId;
                this.processFileUploads(machineId, () => {
                  this.openFinishedDialog();
                });
              } else {
                this.openFinishedDialog();
              }
            },
            (error) => {
              this.submitting = false;
            }
          );
        }
    } catch (e) {
      this.submitting = false;
    }
  }

  shouldUploadFiles(){
    return this.machineAttachments?.length;
  }

  assembleUploads(machineId: string) {
    this.assembleMachineAttachmentPromises(machineId);
  }

  processFileUploads(machineId: string, cb: () => void) {
    this.uploadingFiles = true;
    this.uploadProgress = 0;
    this.files = new FilesUploader(this.fileService, this.loggingService);
    this.files.getProgressSubject().subscribe(val => {
      this.uploadProgress = Math.floor(val);
    });

    this.assembleUploads(machineId);
    this.files.executeSequentially()
      .finally(() => {
        this.uploadingFiles = false;
        cb();
      });
  }

  assembleMachineAttachmentPromises(machineId: string) {
    const machineAttachmentUploader = (file: File, descr: string, fileProgress: Progress) => {
      return this.machineService.uploadAttachment(machineId, file, descr, fileProgress, this.isPublic);
    };
    this.machineAttachments.forEach((item) => {
      this.files.addFile(item, machineAttachmentUploader);
    });
  }

  get isPublic(): boolean{
    return this.appQuery.loginStatus !== LoginStatus.True;
  }

  public addressName(address: CustomerAddressDto): string {
    const addressNameFormat = this.productSettings.stringValue("JobCreateAddressNameFormat");
    return addressName(address, addressNameFormat);
  }
}
